class AnnotationManager {
    constructor() {
        this.canvas = null;
        this.ctx = null;
        this.isDrawing = false;
        this.currentTool = 'pen';
        this.currentColor = '#ef4444';
        this.lineWidth = 3;
        this.annotations = [];
        this.startX = 0;
        this.startY = 0;
        this.currentPath = [];
        this.textInput = null;
        this.currentTextAnnotation = null;
        this.drawingBounds = null;
        this.currentMode = 'fullpage';
        this.initialScrollPosition = null;
        this.markerCounter = 0;
        this.elementHighlight = null;
        this.hoveredElement = null;
    }
    createCanvas() {
        // Always calculate full page dimensions
        const fullPageHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight);
        const fullPageWidth = Math.max(document.body.scrollWidth, document.body.offsetWidth, document.documentElement.clientWidth, document.documentElement.scrollWidth, document.documentElement.offsetWidth);
        // Create canvas with full page dimensions
        this.canvas = document.createElement('canvas');
        this.canvas.className = 'uxbert-canvas-overlay';
        this.canvas.width = fullPageWidth;
        this.canvas.height = fullPageHeight;
        // Set canvas to fullpage mode - absolute positioning
        this.currentMode = 'fullpage';
        this.ctx = this.canvas.getContext('2d');
        if (this.ctx) {
            // Set canvas styles
            this.ctx.lineCap = 'round';
            this.ctx.lineJoin = 'round';
        }
        // Add event listeners
        this.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
        this.canvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
        this.canvas.addEventListener('mouseup', this.handleMouseUp.bind(this));
        this.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this));
        this.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this));
        this.canvas.addEventListener('touchend', this.handleTouchEnd.bind(this));
        document.body.appendChild(this.canvas);
        console.log('✅ Canvas appended to body (FULLPAGE MODE):', {
            canvas: this.canvas,
            className: this.canvas.className,
            width: this.canvas.width,
            height: this.canvas.height,
            fullPageHeight: fullPageHeight,
            fullPageWidth: fullPageWidth,
            inDOM: document.body.contains(this.canvas)
        });
        return this.canvas;
    }
    updateCanvasSize() {
        if (!this.canvas || !this.ctx)
            return;
        // Always use full page dimensions - ensure we get the maximum values
        const fullPageHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight);
        const fullPageWidth = Math.max(document.body.scrollWidth, document.body.offsetWidth, document.documentElement.clientWidth, document.documentElement.scrollWidth, document.documentElement.offsetWidth);
        // Only update if dimensions changed (to avoid clearing canvas unnecessarily)
        if (this.canvas.width !== fullPageWidth || this.canvas.height !== fullPageHeight) {
            console.log('📐 Updating canvas size:', {
                from: { width: this.canvas.width, height: this.canvas.height },
                to: { width: fullPageWidth, height: fullPageHeight }
            });
            // Save current canvas content as image data before resizing
            const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
            // Resize canvas
            this.canvas.width = fullPageWidth;
            this.canvas.height = fullPageHeight;
            // Restore canvas content
            this.ctx.putImageData(imageData, 0, 0);
            // Reset context properties after resize
            this.ctx.lineCap = 'round';
            this.ctx.lineJoin = 'round';
            console.log('✅ Canvas resized and content restored');
        }
    }
    updateDrawingBounds(x, y) {
        if (!this.drawingBounds) {
            this.drawingBounds = { minX: x, minY: y, maxX: x, maxY: y };
        }
        else {
            this.drawingBounds.minX = Math.min(this.drawingBounds.minX, x);
            this.drawingBounds.minY = Math.min(this.drawingBounds.minY, y);
            this.drawingBounds.maxX = Math.max(this.drawingBounds.maxX, x);
            this.drawingBounds.maxY = Math.max(this.drawingBounds.maxY, y);
        }
    }
    show() {
        if (this.canvas && this.ctx) {
            // Always use fullpage mode
            this.currentMode = 'fullpage';
            this.drawingBounds = null;
            // Store initial scroll position
            this.initialScrollPosition = {
                x: window.scrollX,
                y: window.scrollY
            };
            // Update canvas size to match current page dimensions (only if needed)
            this.updateCanvasSize();
            // Ensure context properties are set
            this.ctx.lineCap = 'round';
            this.ctx.lineJoin = 'round';
            // Make canvas visible and interactive
            this.canvas.classList.add('active');
            // Force a repaint to ensure canvas is visible immediately
            void this.canvas.offsetHeight;
            console.log('✅ Canvas shown (FULLPAGE MODE):', {
                mode: 'fullpage',
                hasActiveClass: this.canvas.classList.contains('active'),
                width: this.canvas.width,
                height: this.canvas.height,
                scrollPosition: this.initialScrollPosition,
                computedStyle: window.getComputedStyle(this.canvas).display,
                zIndex: window.getComputedStyle(this.canvas).zIndex,
                pointerEvents: window.getComputedStyle(this.canvas).pointerEvents
            });
            this.redraw();
        }
    }
    hide() {
        if (this.canvas) {
            this.canvas.classList.remove('active');
        }
        this.initialScrollPosition = null;
        // Clean up any active text input
        this.removeTextInput();
        this.currentTextAnnotation = null;
        // Clean up element highlight
        this.hideElementHighlight();
        this.hoveredElement = null;
    }
    setTool(tool) {
        this.currentTool = tool;
    }
    setColor(color) {
        this.currentColor = color;
    }
    handleMouseDown(e) {
        // Ensure canvas size is up to date before drawing starts
        this.updateCanvasSize();
        // Always use scroll offset for fullpage mode (absolute positioning)
        const scrollOffsetX = window.scrollX;
        const scrollOffsetY = window.scrollY;
        // Handle text tool separately
        if (this.currentTool === 'text') {
            const x = e.clientX + scrollOffsetX;
            const y = e.clientY + scrollOffsetY;
            // Update drawing bounds for text
            this.updateDrawingBounds(x, y);
            // Check if clicking on existing text to edit
            const clickedText = this.getTextAnnotationAt(x, y);
            if (clickedText) {
                this.editTextAnnotation(clickedText);
                return;
            }
            // Create new text annotation
            this.createTextInput(x, y);
            return;
        }
        // Handle marker tool - single click to place numbered marker
        if (this.currentTool === 'marker') {
            const x = e.clientX + scrollOffsetX;
            const y = e.clientY + scrollOffsetY;
            this.markerCounter++;
            this.annotations.push({
                tool: 'marker',
                x,
                y,
                number: this.markerCounter,
                color: this.currentColor
            });
            this.updateDrawingBounds(x, y);
            this.redraw();
            return;
        }
        // Handle element selector tool
        if (this.currentTool === 'element') {
            if (this.hoveredElement) {
                const rect = this.hoveredElement.getBoundingClientRect();
                const annotation = {
                    tool: 'element',
                    selector: this.getElementSelector(this.hoveredElement),
                    boundingBox: {
                        x: rect.left + scrollOffsetX,
                        y: rect.top + scrollOffsetY,
                        width: rect.width,
                        height: rect.height
                    },
                    tagName: this.hoveredElement.tagName.toLowerCase(),
                    className: this.hoveredElement.className || undefined,
                    id: this.hoveredElement.id || undefined,
                    color: this.currentColor
                };
                this.annotations.push(annotation);
                this.updateDrawingBounds(annotation.boundingBox.x, annotation.boundingBox.y);
                this.updateDrawingBounds(annotation.boundingBox.x + annotation.boundingBox.width, annotation.boundingBox.y + annotation.boundingBox.height);
                this.redraw();
            }
            return;
        }
        this.isDrawing = true;
        this.startX = e.clientX + scrollOffsetX;
        this.startY = e.clientY + scrollOffsetY;
        this.currentPath = [{ x: this.startX, y: this.startY }];
        // Update drawing bounds with start position
        this.updateDrawingBounds(this.startX, this.startY);
    }
    handleMouseMove(e) {
        // Always use scroll offset for fullpage mode
        const scrollOffsetX = window.scrollX;
        const scrollOffsetY = window.scrollY;
        // Handle element selector hover
        if (this.currentTool === 'element') {
            this.handleElementHover(e);
            return;
        }
        if (!this.isDrawing || !this.ctx)
            return;
        const x = e.clientX + scrollOffsetX;
        const y = e.clientY + scrollOffsetY;
        // Update drawing bounds with current position
        this.updateDrawingBounds(x, y);
        if (this.currentTool === 'pen') {
            this.currentPath.push({ x, y });
            this.redraw();
            this.drawPath(this.currentPath, this.currentColor, this.lineWidth);
        }
        else {
            this.redraw();
            if (this.currentTool === 'arrow') {
                this.drawArrow(this.startX, this.startY, x, y, this.currentColor);
            }
            else if (this.currentTool === 'rectangle') {
                this.drawRectangle(this.startX, this.startY, x, y, this.currentColor);
            }
            else if (this.currentTool === 'circle') {
                this.drawCircle(this.startX, this.startY, x, y, this.currentColor);
            }
            else if (this.currentTool === 'highlight') {
                this.drawHighlight(this.startX, this.startY, x, y, this.currentColor);
            }
            else if (this.currentTool === 'blur') {
                this.drawBlurPreview(this.startX, this.startY, x, y, this.currentColor);
            }
        }
    }
    handleMouseUp(e) {
        if (!this.isDrawing)
            return;
        this.isDrawing = false;
        // Always use scroll offset for fullpage mode
        const scrollOffsetX = window.scrollX;
        const scrollOffsetY = window.scrollY;
        const x = e.clientX + scrollOffsetX;
        const y = e.clientY + scrollOffsetY;
        // Update drawing bounds with final position
        this.updateDrawingBounds(x, y);
        // Save the annotation
        if (this.currentTool === 'pen') {
            this.annotations.push({
                tool: 'pen',
                path: this.currentPath,
                color: this.currentColor,
                lineWidth: this.lineWidth
            });
        }
        else if (this.currentTool === 'arrow') {
            this.annotations.push({
                tool: 'arrow',
                startX: this.startX,
                startY: this.startY,
                endX: x,
                endY: y,
                color: this.currentColor
            });
        }
        else if (this.currentTool === 'rectangle') {
            this.annotations.push({
                tool: 'rectangle',
                startX: this.startX,
                startY: this.startY,
                endX: x,
                endY: y,
                color: this.currentColor
            });
        }
        else if (this.currentTool === 'circle') {
            const centerX = (this.startX + x) / 2;
            const centerY = (this.startY + y) / 2;
            const radius = Math.sqrt(Math.pow(x - this.startX, 2) + Math.pow(y - this.startY, 2)) / 2;
            this.annotations.push({
                tool: 'circle',
                centerX,
                centerY,
                radius,
                color: this.currentColor
            });
        }
        else if (this.currentTool === 'highlight') {
            this.annotations.push({
                tool: 'highlight',
                startX: this.startX,
                startY: this.startY,
                endX: x,
                endY: y,
                color: this.currentColor
            });
        }
        else if (this.currentTool === 'blur') {
            this.annotations.push({
                tool: 'blur',
                startX: this.startX,
                startY: this.startY,
                endX: x,
                endY: y,
                color: this.currentColor
            });
        }
        this.redraw();
    }
    handleTouchStart(e) {
        e.preventDefault();
        if (!this.canvas)
            return;
        const touch = e.touches[0];
        const rect = this.canvas.getBoundingClientRect();
        // Calculate coordinates accounting for canvas position and viewport
        // For fullpage mode, we need to add scroll offset but subtract canvas offset
        const clientX = touch.clientX - rect.left + window.scrollX;
        const clientY = touch.clientY - rect.top + window.scrollY;
        this.handleMouseDown({
            clientX: clientX,
            clientY: clientY
        });
    }
    handleTouchMove(e) {
        e.preventDefault();
        if (!this.canvas)
            return;
        const touch = e.touches[0];
        const rect = this.canvas.getBoundingClientRect();
        // Calculate coordinates accounting for canvas position and viewport
        const clientX = touch.clientX - rect.left + window.scrollX;
        const clientY = touch.clientY - rect.top + window.scrollY;
        this.handleMouseMove({
            clientX: clientX,
            clientY: clientY
        });
    }
    handleTouchEnd(e) {
        e.preventDefault();
        if (!this.canvas)
            return;
        const touch = e.changedTouches[0];
        const rect = this.canvas.getBoundingClientRect();
        // Calculate coordinates accounting for canvas position and viewport
        const clientX = touch.clientX - rect.left + window.scrollX;
        const clientY = touch.clientY - rect.top + window.scrollY;
        this.handleMouseUp({
            clientX: clientX,
            clientY: clientY
        });
    }
    drawPath(path, color, lineWidth) {
        if (!this.ctx || path.length < 2)
            return;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = lineWidth;
        this.ctx.beginPath();
        this.ctx.moveTo(path[0].x, path[0].y);
        for (let i = 1; i < path.length; i++) {
            this.ctx.lineTo(path[i].x, path[i].y);
        }
        this.ctx.stroke();
    }
    drawArrow(x1, y1, x2, y2, color) {
        if (!this.ctx)
            return;
        const headLength = 20;
        const angle = Math.atan2(y2 - y1, x2 - x1);
        // Draw line
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.lineWidth;
        this.ctx.beginPath();
        this.ctx.moveTo(x1, y1);
        this.ctx.lineTo(x2, y2);
        this.ctx.stroke();
        // Draw arrowhead
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(x2, y2);
        this.ctx.lineTo(x2 - headLength * Math.cos(angle - Math.PI / 6), y2 - headLength * Math.sin(angle - Math.PI / 6));
        this.ctx.lineTo(x2 - headLength * Math.cos(angle + Math.PI / 6), y2 - headLength * Math.sin(angle + Math.PI / 6));
        this.ctx.closePath();
        this.ctx.fill();
    }
    drawRectangle(x1, y1, x2, y2, color) {
        if (!this.ctx)
            return;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.lineWidth;
        this.ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
    }
    drawCircle(x1, y1, x2, y2, color) {
        if (!this.ctx)
            return;
        const centerX = (x1 + x2) / 2;
        const centerY = (y1 + y2) / 2;
        const radius = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)) / 2;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.lineWidth;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        this.ctx.stroke();
    }
    drawCircleFromCenter(centerX, centerY, radius, color) {
        if (!this.ctx)
            return;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.lineWidth;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        this.ctx.stroke();
    }
    drawHighlight(x1, y1, x2, y2, color) {
        if (!this.ctx)
            return;
        // Convert hex color to RGB with alpha for semi-transparency
        const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };
        // Draw semi-transparent filled rectangle
        this.ctx.fillStyle = hexToRgba(color, 0.3);
        this.ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
        // Add subtle border for better visibility
        this.ctx.strokeStyle = hexToRgba(color, 0.6);
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
    }
    drawBlurPreview(x1, y1, x2, y2, color) {
        if (!this.ctx)
            return;
        const width = x2 - x1;
        const height = y2 - y1;
        // Draw preview rectangle with dashed border
        this.ctx.setLineDash([5, 5]);
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(x1, y1, width, height);
        this.ctx.setLineDash([]);
        // Draw crosshatch pattern to indicate blur area
        this.ctx.fillStyle = 'rgba(128, 128, 128, 0.3)';
        this.ctx.fillRect(x1, y1, width, height);
    }
    drawBlur(x1, y1, x2, y2) {
        if (!this.ctx || !this.canvas)
            return;
        const width = Math.abs(x2 - x1);
        const height = Math.abs(y2 - y1);
        const startX = Math.min(x1, x2);
        const startY = Math.min(y1, y2);
        // Create pixelated/mosaic blur effect
        const pixelSize = 10;
        // Get image data from the area
        try {
            const imageData = this.ctx.getImageData(startX, startY, width, height);
            const data = imageData.data;
            // Pixelate the area
            for (let y = 0; y < height; y += pixelSize) {
                for (let x = 0; x < width; x += pixelSize) {
                    // Get average color of the block
                    let r = 0, g = 0, b = 0, count = 0;
                    for (let dy = 0; dy < pixelSize && y + dy < height; dy++) {
                        for (let dx = 0; dx < pixelSize && x + dx < width; dx++) {
                            const i = ((y + dy) * width + (x + dx)) * 4;
                            if (i < data.length) {
                                r += data[i];
                                g += data[i + 1];
                                b += data[i + 2];
                                count++;
                            }
                        }
                    }
                    if (count > 0) {
                        r = Math.floor(r / count);
                        g = Math.floor(g / count);
                        b = Math.floor(b / count);
                        // Fill the block with average color
                        for (let dy = 0; dy < pixelSize && y + dy < height; dy++) {
                            for (let dx = 0; dx < pixelSize && x + dx < width; dx++) {
                                const i = ((y + dy) * width + (x + dx)) * 4;
                                if (i < data.length) {
                                    data[i] = r;
                                    data[i + 1] = g;
                                    data[i + 2] = b;
                                }
                            }
                        }
                    }
                }
            }
            this.ctx.putImageData(imageData, startX, startY);
        }
        catch {
            // Fallback: draw solid gray rectangle if getImageData fails (cross-origin)
            this.ctx.fillStyle = 'rgba(128, 128, 128, 0.8)';
            this.ctx.fillRect(startX, startY, width, height);
        }
        // Draw border around blurred area
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(startX, startY, width, height);
    }
    drawMarker(x, y, number, color) {
        if (!this.ctx)
            return;
        const radius = 16;
        // Draw shadow
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        this.ctx.shadowBlur = 4;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 2;
        // Draw circle background
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, Math.PI * 2);
        this.ctx.fill();
        // Reset shadow
        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 0;
        // Draw white border
        this.ctx.strokeStyle = '#ffffff';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, Math.PI * 2);
        this.ctx.stroke();
        // Draw number
        this.ctx.fillStyle = '#ffffff';
        this.ctx.font = 'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(String(number), x, y);
    }
    drawElementBox(boundingBox, color, tagName) {
        if (!this.ctx)
            return;
        const { x, y, width, height } = boundingBox;
        // Draw dashed border
        this.ctx.setLineDash([4, 4]);
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(x, y, width, height);
        this.ctx.setLineDash([]);
        // Draw corner markers
        const cornerSize = 8;
        this.ctx.fillStyle = color;
        // Top-left
        this.ctx.fillRect(x - 2, y - 2, cornerSize, 3);
        this.ctx.fillRect(x - 2, y - 2, 3, cornerSize);
        // Top-right
        this.ctx.fillRect(x + width - cornerSize + 2, y - 2, cornerSize, 3);
        this.ctx.fillRect(x + width - 1, y - 2, 3, cornerSize);
        // Bottom-left
        this.ctx.fillRect(x - 2, y + height - 1, cornerSize, 3);
        this.ctx.fillRect(x - 2, y + height - cornerSize + 2, 3, cornerSize);
        // Bottom-right
        this.ctx.fillRect(x + width - cornerSize + 2, y + height - 1, cornerSize, 3);
        this.ctx.fillRect(x + width - 1, y + height - cornerSize + 2, 3, cornerSize);
        // Draw tag label
        const labelPadding = 4;
        const labelHeight = 18;
        this.ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        const labelWidth = this.ctx.measureText(tagName).width + labelPadding * 2;
        // Label background
        this.ctx.fillStyle = color;
        this.ctx.fillRect(x, y - labelHeight - 2, labelWidth, labelHeight);
        // Label text
        this.ctx.fillStyle = '#ffffff';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(tagName, x + labelPadding, y - labelHeight / 2 - 2);
    }
    handleElementHover(e) {
        // Hide canvas temporarily to get element underneath
        if (this.canvas) {
            this.canvas.style.pointerEvents = 'none';
        }
        const element = document.elementFromPoint(e.clientX, e.clientY);
        if (this.canvas) {
            this.canvas.style.pointerEvents = 'auto';
        }
        // Ignore our own UI elements
        if (element && !element.closest('[data-uxbert-aminah]') && element !== this.canvas) {
            this.hoveredElement = element;
            this.showElementHighlight(element);
        }
        else {
            this.hoveredElement = null;
            this.hideElementHighlight();
        }
    }
    showElementHighlight(element) {
        if (!this.elementHighlight) {
            this.elementHighlight = document.createElement('div');
            this.elementHighlight.className = 'uxbert-element-highlight';
            this.elementHighlight.setAttribute('data-uxbert-aminah', '');
            document.body.appendChild(this.elementHighlight);
        }
        const rect = element.getBoundingClientRect();
        this.elementHighlight.style.cssText = `
      position: fixed;
      left: ${rect.left}px;
      top: ${rect.top}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      border: 2px dashed ${this.currentColor};
      background: ${this.hexToRgba(this.currentColor, 0.1)};
      pointer-events: none;
      z-index: 2147483646;
      box-sizing: border-box;
    `;
    }
    hideElementHighlight() {
        if (this.elementHighlight) {
            this.elementHighlight.remove();
            this.elementHighlight = null;
        }
    }
    getElementSelector(element) {
        if (element.id) {
            return `#${element.id}`;
        }
        const tagName = element.tagName.toLowerCase();
        const className = element.className && typeof element.className === 'string'
            ? element.className.trim().split(/\s+/).filter(c => c).join('.')
            : '';
        if (className) {
            return `${tagName}.${className}`;
        }
        return tagName;
    }
    hexToRgba(hex, alpha) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    createTextInput(x, y) {
        // Remove existing input if any
        this.removeTextInput();
        // Create input element
        this.textInput = document.createElement('input');
        this.textInput.type = 'text';
        this.textInput.className = 'uxbert-text-input';
        // Position relative to the document (absolute positioning)
        this.textInput.style.left = `${x}px`;
        this.textInput.style.top = `${y}px`;
        this.textInput.style.transform = 'translate(0, 0)';
        this.textInput.placeholder = 'Enter text...';
        this.textInput.autocomplete = 'off';
        // Create new annotation object
        this.currentTextAnnotation = {
            tool: 'text',
            x: x,
            y: y,
            text: '',
            color: this.currentColor
        };
        // Add to document
        document.body.appendChild(this.textInput);
        // Focus with a small delay to ensure it's rendered
        setTimeout(() => {
            this.textInput?.focus();
            this.textInput?.select();
        }, 10);
        // Handle input events
        const blurHandler = () => {
            // Use setTimeout to allow other events to process first
            setTimeout(() => {
                this.saveTextInput();
            }, 100);
        };
        const keyHandler = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.saveTextInput();
            }
            else if (e.key === 'Escape') {
                e.preventDefault();
                this.removeTextInput();
                this.currentTextAnnotation = null;
            }
        };
        this.textInput.addEventListener('blur', blurHandler);
        this.textInput.addEventListener('keydown', keyHandler);
        // Store handlers for cleanup
        this.textInput._blurHandler = blurHandler;
        this.textInput._keyHandler = keyHandler;
    }
    editTextAnnotation(annotation) {
        // Remove from annotations array temporarily
        const index = this.annotations.findIndex(a => a.tool === 'text' && a.x === annotation.x && a.y === annotation.y && a.text === annotation.text);
        if (index > -1) {
            this.annotations.splice(index, 1);
        }
        // Create input with existing text
        this.createTextInput(annotation.x, annotation.y);
        if (this.textInput) {
            this.textInput.value = annotation.text;
        }
        this.currentTextAnnotation = annotation;
        this.redraw();
    }
    saveTextInput() {
        if (this.textInput && this.currentTextAnnotation) {
            const text = this.textInput.value.trim();
            if (text) {
                this.currentTextAnnotation.text = text;
                this.annotations.push(this.currentTextAnnotation);
                this.redraw();
            }
        }
        this.removeTextInput();
        this.currentTextAnnotation = null;
    }
    removeTextInput() {
        if (this.textInput) {
            // Remove event listeners
            if (this.textInput._blurHandler) {
                this.textInput.removeEventListener('blur', this.textInput._blurHandler);
            }
            if (this.textInput._keyHandler) {
                this.textInput.removeEventListener('keydown', this.textInput._keyHandler);
            }
            // Remove from DOM
            if (this.textInput.parentNode) {
                this.textInput.parentNode.removeChild(this.textInput);
            }
            this.textInput = null;
        }
    }
    getTextAnnotationAt(x, y) {
        if (!this.ctx)
            return null;
        // Check if click is within any text annotation bounds
        this.ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
        for (let i = this.annotations.length - 1; i >= 0; i--) {
            const annotation = this.annotations[i];
            if (annotation.tool === 'text') {
                const metrics = this.ctx.measureText(annotation.text);
                const padding = 10;
                const left = annotation.x - padding;
                const top = annotation.y - padding;
                const width = metrics.width + padding * 2;
                const height = 28 + padding * 2;
                if (x >= left && x <= left + width && y >= top && y <= top + height) {
                    return annotation;
                }
            }
        }
        return null;
    }
    drawText(x, y, text, color) {
        if (!this.ctx)
            return;
        // Enhanced font styling with better typography
        this.ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
        this.ctx.textBaseline = 'top';
        // Measure text for precise positioning
        const metrics = this.ctx.measureText(text);
        const padding = 10;
        const borderRadius = 6;
        // Calculate dimensions
        const boxWidth = metrics.width + padding * 2;
        const boxHeight = 28 + padding * 2;
        // Draw shadow for depth
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
        this.ctx.shadowBlur = 8;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 2;
        // Draw rounded rectangle background
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        this.ctx.beginPath();
        this.ctx.roundRect(x - padding, y - padding, boxWidth, boxHeight, borderRadius);
        this.ctx.fill();
        // Reset shadow for border
        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 0;
        // Draw subtle border with matching color
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.roundRect(x - padding, y - padding, boxWidth, boxHeight, borderRadius);
        this.ctx.stroke();
        // Add subtle text shadow for better readability
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
        this.ctx.shadowBlur = 2;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 1;
        // Draw text with selected color
        this.ctx.fillStyle = color;
        this.ctx.fillText(text, x, y);
        // Reset shadow
        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 0;
    }
    redraw() {
        if (!this.ctx || !this.canvas)
            return;
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        // Redraw all saved annotations
        for (const annotation of this.annotations) {
            if (annotation.tool === 'pen') {
                this.drawPath(annotation.path, annotation.color, annotation.lineWidth);
            }
            else if (annotation.tool === 'arrow') {
                this.drawArrow(annotation.startX, annotation.startY, annotation.endX, annotation.endY, annotation.color);
            }
            else if (annotation.tool === 'rectangle') {
                this.drawRectangle(annotation.startX, annotation.startY, annotation.endX, annotation.endY, annotation.color);
            }
            else if (annotation.tool === 'circle') {
                this.drawCircleFromCenter(annotation.centerX, annotation.centerY, annotation.radius, annotation.color);
            }
            else if (annotation.tool === 'highlight') {
                this.drawHighlight(annotation.startX, annotation.startY, annotation.endX, annotation.endY, annotation.color);
            }
            else if (annotation.tool === 'blur') {
                this.drawBlur(annotation.startX, annotation.startY, annotation.endX, annotation.endY);
            }
            else if (annotation.tool === 'marker') {
                this.drawMarker(annotation.x, annotation.y, annotation.number, annotation.color);
            }
            else if (annotation.tool === 'element') {
                this.drawElementBox(annotation.boundingBox, annotation.color, annotation.tagName);
            }
            else if (annotation.tool === 'text') {
                this.drawText(annotation.x, annotation.y, annotation.text, annotation.color);
            }
        }
    }
    undo() {
        if (this.annotations.length > 0) {
            this.annotations.pop();
            this.redraw();
        }
    }
    clear() {
        this.annotations = [];
        this.drawingBounds = null;
        this.markerCounter = 0;
        this.redraw();
    }
    getCurrentMode() {
        return this.currentMode;
    }
    async exportAnnotatedScreenshot(baseScreenshot) {
        // Create a temporary canvas to combine screenshot + annotations
        const tempCanvas = document.createElement('canvas');
        const img = new Image();
        img.src = baseScreenshot;
        return new Promise((resolve) => {
            img.onload = () => {
                // Match the canvas size to the screenshot dimensions
                tempCanvas.width = img.width;
                tempCanvas.height = img.height;
                const tempCtx = tempCanvas.getContext('2d');
                if (tempCtx && this.canvas) {
                    // Draw the base screenshot
                    tempCtx.drawImage(img, 0, 0);
                    console.log('📸 Exporting annotated screenshot (FULLPAGE MODE):', {
                        mode: 'fullpage',
                        scrollPosition: { x: window.scrollX, y: window.scrollY },
                        canvasSize: { width: this.canvas.width, height: this.canvas.height },
                        screenshotSize: { width: img.width, height: img.height }
                    });
                    // Fullpage mode - annotations and screenshot are both in absolute page coordinates
                    // Just need to handle potential scaling due to device pixel ratio
                    const scaleX = img.width / this.canvas.width;
                    const scaleY = img.height / this.canvas.height;
                    if (scaleX !== 1 || scaleY !== 1) {
                        tempCtx.save();
                        tempCtx.scale(scaleX, scaleY);
                        tempCtx.drawImage(this.canvas, 0, 0);
                        tempCtx.restore();
                    }
                    else {
                        tempCtx.drawImage(this.canvas, 0, 0);
                    }
                    resolve(tempCanvas.toDataURL('image/png'));
                }
            };
        });
    }
    destroy() {
        if (this.canvas && this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas);
            this.canvas = null;
            this.ctx = null;
        }
    }
}
export default AnnotationManager;
//# sourceMappingURL=annotation.js.map